import { useState } from 'react'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { CompactTable, SectionTitle } from 'shared/components/section'
import { Button } from 'shared/components/ui/button'
import { Input } from 'shared/components/ui/input'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shared/components/ui/table'
import { useTranslate } from 'shared/lib/i18n'
import { scheduleOfRows, weekRowsOf, type DayRow, type ScheduleDay } from '../lib/week'

/**
 * A Point's week on «Локации» (ticket 45, user story 132): each weekday ticked or a
 * day off, its hours and an optional lunch, Tashkent time. The administrator of
 * TezGo leads it; `back`'s week was only its first load. The server checks the rules
 * and names a broken one in words. The week forbids nothing — it is shown to the
 * operator, the driver and the hub.
 *
 * `save` is injected so the form is tested without a backend.
 */
export type SetScheduleAnswer = RouterOutputs['locations']['SetSchedule']
type SetScheduleRequest = RouterInputs['locations']['SetSchedule']

const saveViaTrpc = (input: SetScheduleRequest) => trpcClient.locations.SetSchedule.mutate(input)

export function PointWeek({
	locationId,
	schedule,
	onChanged,
	save = saveViaTrpc,
}: {
	locationId: string
	schedule: ScheduleDay[] | null
	onChanged?: () => void
	save?: (input: SetScheduleRequest) => Promise<SetScheduleAnswer>
}) {
	const { t } = useTranslate()
	const [rows, setRows] = useState<DayRow[]>(() => weekRowsOf(schedule))
	const [busy, setBusy] = useState(false)
	const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)

	const change = (day: number, patch: Partial<DayRow>) => {
		setRows((all) => all.map((r) => (r.day === day ? { ...r, ...patch } : r)))
		setMessage(null)
	}

	const onSave = async () => {
		if (busy) return
		setBusy(true)
		setMessage(null)
		try {
			const answer = await save({ _id: locationId, schedule: scheduleOfRows(rows) })
			if (answer.outcome === 'refused') {
				setMessage({ ok: false, text: t(`pointWeek.refused_${answer.reason}` as never) })
			} else {
				setMessage({ ok: true, text: answer.outcome === 'set' ? t('pointWeek.saved') : t('pointWeek.unchanged') })
				onChanged?.()
			}
		} catch (error) {
			setMessage({ ok: false, text: t('pointClosure.failed', { message: error instanceof Error ? error.message : String(error) }) })
		} finally {
			setBusy(false)
		}
	}

	const timeInput = (r: DayRow, key: 'from' | 'to' | 'lunchFrom' | 'lunchTo', testKey: string, label: string, placeholder: string) => (
		<Input
			id={key === 'from' ? `week-${locationId}-${r.day}-from` : undefined}
			className='h-8 w-20 tabular-nums'
			aria-label={label}
			value={r[key]}
			disabled={!r.on}
			placeholder={placeholder}
			data-testid={`point-week-${testKey}-${r.day}`}
			onChange={(e) => change(r.day, { [key]: e.target.value })}
		/>
	)

	return (
		<section className='flex flex-col gap-2' data-testid='point-week'>
			<SectionTitle
				title={t('pointWeek.title')}
				hint={t('pointWeek.hint')}
				aside={
					<Button size='sm' variant='outline' disabled={busy} onClick={onSave} data-testid='point-week-save'>
						{t('pointWeek.save')}
					</Button>
				}
			/>
			<CompactTable>
				<TableHeader>
					<TableRow>
						<TableHead>{t('pointWeek.day')}</TableHead>
						<TableHead>{t('pointWeek.hours')}</TableHead>
						<TableHead>{t('pointWeek.lunchCol')}</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{rows.map((r) => (
						<TableRow key={r.day} data-testid={`point-week-day-${r.day}`} className='hover:bg-transparent'>
							<TableCell className='w-32 py-1'>
								<label className='flex items-center gap-2'>
									<input
										type='checkbox'
										className='size-4 accent-primary'
										checked={r.on}
										aria-label={t('pointWeek.workDay')}
										data-testid={`point-week-on-${r.day}`}
										onChange={(e) => change(r.day, { on: e.target.checked })}
									/>
									<span className={r.on ? '' : 'text-muted-foreground'}>{t(`weekday.${r.day}` as never)}</span>
								</label>
							</TableCell>
							<TableCell className='py-1'>
								<div className='flex items-center gap-1.5'>
									{timeInput(r, 'from', 'from', t('pointWeek.from'), '09:00')}
									<span className='text-muted-foreground'>–</span>
									{timeInput(r, 'to', 'to', t('pointWeek.to'), '19:00')}
								</div>
							</TableCell>
							<TableCell className='py-1'>
								<div className='flex items-center gap-1.5'>
									{timeInput(r, 'lunchFrom', 'lunch-from', t('pointWeek.lunchFrom'), '13:00')}
									<span className='text-muted-foreground'>–</span>
									{timeInput(r, 'lunchTo', 'lunch-to', t('pointWeek.lunchTo'), '14:00')}
								</div>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</CompactTable>
			{message && (
				<p
					className={message.ok ? 'text-sm text-emerald-700 dark:text-emerald-400' : 'text-sm text-destructive'}
					role={message.ok ? 'status' : 'alert'}
					data-testid='point-week-message'
				>
					{message.text}
				</p>
			)}
		</section>
	)
}
